/**
 * Single source of truth for how many exercises a workout day may hold.
 *
 * Reused by every surface that adds exercises to a day — manual + automatic
 * challenge plans (Group, H2H, Pair, Train Together), the individual Home
 * plan, generated/scanned AI plans and ad-hoc workouts — so the rule never
 * drifts per feature.
 */

export const MIN_EXERCISES_PER_DAY = 1;
export const MAX_EXERCISES_PER_DAY = 10;

/**
 * Superset / tri-set / giant-set group size (SOLO personal plans only).
 * A group is a flexible list of linked exercises, never a fixed pair.
 */
export const MIN_SUPERSET_SIZE = 2;
export const MAX_SUPERSET_SIZE = 4;

export const SUPERSET_SIZE_MESSAGE = `A superset needs ${MIN_SUPERSET_SIZE}-${MAX_SUPERSET_SIZE} exercises.`;

/** Reject a group that is too small or too large. */
export function assertSupersetSize(count: number): void {
  if (count < MIN_SUPERSET_SIZE || count > MAX_SUPERSET_SIZE) throw new Error(SUPERSET_SIZE_MESSAGE);
}

/** Label for a group of N linked exercises. */
export function supersetLabel(count: number): string {
  if (count >= 4) return "Giant set";
  if (count === 3) return "Tri-set";
  return "Superset";
}

/** Error message shown when a save would push a day past the cap. */
export const DAY_CAP_MESSAGE = `You can add up to ${MAX_EXERCISES_PER_DAY} exercises per day.`;
/** Error message when a training (non-rest) day has no exercises at all. */
export const DAY_MIN_MESSAGE = "A training day needs at least 1 exercise (or mark it a rest day).";


/** True when no further exercise may be added to a day of this size. */
export function isDayFull(count: number): boolean {
  return count >= MAX_EXERCISES_PER_DAY;
}

/** How many more exercises fit on a day of this size. */
export function remainingSlots(count: number): number {
  return Math.max(0, MAX_EXERCISES_PER_DAY - count);
}

/**
 * Server-side guard: reject a saved day that exceeds the cap.
 * `allowEmpty` is for explicit rest days, which legitimately have 0 exercises.
 */
export function assertDayExerciseCount(count: number, opts?: { allowEmpty?: boolean }): void {
  if (count > MAX_EXERCISES_PER_DAY) throw new Error(DAY_CAP_MESSAGE);
  if (!opts?.allowEmpty && count < MIN_EXERCISES_PER_DAY) throw new Error(DAY_MIN_MESSAGE);
}

/** Guard for a single incremental add against an existing day. */
export function assertCanAddExercise(currentCount: number): void {
  if (isDayFull(currentCount)) throw new Error(DAY_CAP_MESSAGE);
}

/** Truncate a bulk/AI-authored day to the cap (never mutates the input). */
export function capDayExercises<T>(list: readonly T[]): T[] {
  return list.slice(0, MAX_EXERCISES_PER_DAY);
}

/** Truncate every day of a whole plan-shaped object to the cap. */
export function capPlanExerciseDays<
  P extends { workout_plan?: Array<{ exercises?: unknown[] }> | undefined },
>(plan: P): P {
  if (!plan || !Array.isArray(plan.workout_plan)) return plan;
  for (const day of plan.workout_plan) {
    if (Array.isArray(day?.exercises) && day.exercises.length > MAX_EXERCISES_PER_DAY) {
      day.exercises = capDayExercises(day.exercises);
    }
  }
  return plan;
}
